/**
 * The 11Deeds desktop app for Windows and Mac (11deeds_Desktop, Electron) shows
 * this web app in a window and hands it `window.desktop` from its preload. In a
 * browser there is no bridge and every helper here does nothing. The desktop app
 * and the web app ship separately, so every member is checked before use: an
 * older desktop app may lack one.
 *
 * The desktop repo's `pnpm check` compares this interface with its preload
 * (`check:bridge`): add a member there first, then type it here.
 */
export interface DesktopBridge {
  bridgeVersion: number;
  version: string;
  /** `process.platform` of the computer: "win32" or "darwin". */
  platform: string;
  /**
   * A native notification; clicking it opens `link` (an in-app path) in the window.
   * `silent` leaves out the Windows sound when the page plays its own (shells before
   * 1.0.2 ignore it).
   */
  notify?: (n: {
    title: string;
    body?: string | null;
    link?: string | null;
    silent?: boolean;
  }) => void;
  /** The red dot on the taskbar button and the tray icon. */
  setUnreadCount?: (count: number) => void;
  /** The desktop app asks for an in-app path (a notification click, the morning clock). */
  onOpenLink?: (cb: (path: string) => void) => () => void;
  /**
   * The person is back at the PC: the app started, the screen was unlocked, the
   * PC woke, or they came back after a long idle spell. `reason` says which.
   */
  onWake?: (cb: (reason: string) => void) => () => void;
  /**
   * Whether the signed-in person has a clock. False for someone from outside the
   * company: the desktop app then never opens the clock in the morning. Since 1.0.2.
   */
  setUsesClock?: (on: boolean) => void;
  /**
   * The desktop app's title bar, in the sidebar's colours so the two read as one
   * surface (Slack's look). Each a `#rrggbb`. Sent again whenever the theme
   * changes. Since 1.0.2.
   */
  setTitleBarColors?: (c: { background: string; text: string }) => void;
  /**
   * Today's work clock, for the tray: its state, the seconds worked as of now (the
   * app counts on from there while working) and when the day started, already in
   * the person's timezone ("09:12"). `null` when nobody is signed in. Since 1.1.0.
   */
  setClockState?: (s: DesktopClockState | null) => void;
  /**
   * The tray asks for a clock move. `in` and `out` come with the window in front
   * (clock-in asks its questions, clock-out opens its dialog); `break` and
   * `resume` may come while it is hidden. Returns the function that stops
   * listening. Since 1.1.0.
   */
  onClockAction?: (cb: (action: DesktopClockAction) => void) => () => void;
  /**
   * The running task timer's occurrence id, or `null` when none runs. The app
   * shows its mini timer while one runs and the window is not in front, and
   * tells the mini timer to refresh. Sent by the main window. Since 1.1.0.
   */
  setActiveRun?: (occurrenceId: string | null) => void;
  /** Mini timer only: the running timer changed; refetch it. Since 1.1.0. */
  onActiveRun?: (cb: () => void) => () => void;
  /** Mini timer only: grow the window for a dialog (Pause, Complete), or back. Since 1.1.0. */
  setMiniExpanded?: (on: boolean) => void;
  /**
   * The hour to check in for (the API's `promptFor`) and the company's snooze, or
   * `null`: the app's hourly prompt comes up for it on top of other apps. Sent by
   * the main window. Since 1.1.0.
   */
  setHourlyDue?: (d: { start: string; snoozeMin: number } | null) => void;
  /**
   * A clock-in the person made on the app's offline page: when this computer said
   * it happened. Sent as an offline replay (`clientClaimedAt`) once the app is
   * back; it waits until `offlineClockInDone`. Since 1.1.0.
   */
  takeOfflineClockIn?: () => { id: string; claimedAt: string } | null;
  /** The offline clock-in was sent, or refused for good: it waits no more. Since 1.1.0. */
  offlineClockInDone?: (id: string, outcome: 'sent' | 'refused') => void;
  /** Hourly prompt only: sent or skipped (`done`), or put off (`later`). Since 1.1.0. */
  closePrompt?: (how: 'done' | 'later') => void;
  /** Mini timer only: hide it until the next timer starts. Since 1.1.0. */
  hideMini?: () => void;
  /** Mini timer only: bring the 11Deeds window to the front on an in-app path. Since 1.1.0. */
  showMain?: (path: string) => void;
}

export type DesktopClockAction = 'in' | 'break' | 'resume' | 'out';

export interface DesktopClockState {
  state: 'not_started' | 'working' | 'break' | 'out';
  workedSeconds: number;
  since: string | null;
}

declare global {
  interface Window {
    desktop?: DesktopBridge;
  }
}

export function desktop(): DesktopBridge | undefined {
  return typeof window === 'undefined' ? undefined : window.desktop;
}

/** True inside the desktop app. */
export function isDesktop(): boolean {
  return desktop() !== undefined;
}
